import { useState } from 'react'
import { useFetcher, useLoaderData, useSubmit } from 'react-router'
import { HoldToConfirmButton } from '#app/components/HoldToConfirmButton.tsx'
import { QuickbooksCombobox } from '#app/components/quickbooks-combobox.tsx'
import { Button } from '#app/components/ui/button'
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '#app/components/ui/card'
import { Icon } from '#app/components/ui/icon'
import { Input } from '#app/components/ui/input.tsx'
import { Separator } from '#app/components/ui/separator'
import { StatusButton } from '#app/components/ui/status-button'
import { useDoubleCheck } from '#app/utils/misc.tsx'
import { useOptionalAdminUser } from '#app/utils/user.ts'
import {
	type loader,
	type action,
	type UserType,
	type Transaction,
	type ChangesType,
} from './credits.server.tsx'

export { loader, action } from './credits.server.tsx'

export default function ViewTransactions() {
	const {
		schedule,
		transactions,
		creditsTotal,
		quickbooksMap,
		newUserOptions,
	} = useLoaderData<typeof loader>()
	const userIsAdmin = useOptionalAdminUser()
	const USDollar = new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
	})

	const submit = useSubmit()
	const [newUser, setNewUser] = useState({} as UserType)
	const [addNew, setAddNew] = useState(false)
	const [transaction, setTransaction] = useState({} as Transaction)

	const handleQuickbooksSelect = (quickbooks: string) => {
		const selectedUser = quickbooksMap[quickbooks] ?? ({} as UserType)
		setNewUser(selectedUser)
	}
	const handleTransactionValueChanged = (field: string, value: string) =>
		setTransaction({ ...transaction, [field]: value })
	const saveNewDisabled =
		newUser.id && transaction.type && transaction.debit && transaction.note
			? false
			: true
	const handleSaveNew = async () => {
		const submission = {
			intent: 'create-transaction',
			userId: newUser.id,
			scheduleId: schedule.id,
			date: schedule.date,
			type: transaction.type,
			debit: transaction.debit,
			note: transaction.note,
		}
		await submit(submission, { method: 'POST' })
		setNewUser({} as UserType)
		setTransaction({} as Transaction)
		setAddNew(false)
	}

	const fetcher = useFetcher()
	const handleChange = async (changes: ChangesType) =>
		await fetcher.submit(changes, { method: 'POST' })

	return (
		<Card className="m-auto mt-2 flex w-full flex-col items-center justify-center gap-1 rounded-none bg-accent px-0 pb-4">
			<CardHeader className="flex w-full flex-col flex-wrap items-center gap-2 self-center p-4">
				<CardTitle className="text-h2">
					Irrigation Credits for {schedule.date}
				</CardTitle>
				<CardDescription className="flex w-full justify-between pr-8">
					<div className="w-36"></div>
					<div className="text-xl">
						Credits Total: {USDollar.format(creditsTotal)}
					</div>
					{userIsAdmin ? (
						<Button
							className="w-32"
							variant="secondary"
							onClick={() => setAddNew(!addNew)}
						>
							New Credit
						</Button>
					) : null}
				</CardDescription>
			</CardHeader>
			<CardContent className="w-full space-y-2">
				{addNew ? (
					<div key="create-transaction">
						<div className="grid grid-cols-12 gap-1">
							<div className="col-span-1 px-3 text-lg">Display</div>
							<div className="col-span-2 px-3 text-lg">Quickbooks</div>
							<div className="col-span-2 px-3 text-lg">Primary Email</div>
							<div className="col-span-1 px-3 text-lg">Type</div>
							<div className="col-span-1 px-3 text-right text-lg">Amount</div>
							<div className="col-span-4 px-3 text-lg">Note</div>
						</div>
						<div
							key="row-create"
							className="grid grid-cols-12 gap-1 disabled:cursor-default"
						>
							<button type="submit" className="hidden" />
							<input type="hidden" name="scheduleId" value={schedule.id} />
							<input type="hidden" name="date" value={schedule.date} />
							<input type="hidden" name="userId" value={newUser.id} />
							<Input
								id="display"
								className="col-span-1 disabled:cursor-default"
								disabled={true}
								defaultValue={newUser.display}
							/>
							<div key="quickbooks" className="col-span-2">
								<QuickbooksCombobox
									options={newUserOptions}
									value={newUser.quickbooks?.toLowerCase() ?? ''}
									setValue={handleQuickbooksSelect}
								/>
							</div>
							<Input
								id="primaryEmail"
								className="col-span-2 disabled:cursor-default"
								disabled={true}
								defaultValue={newUser.primaryEmail}
							/>
							<Input
								id="type"
								className="col-span-1"
								value={transaction.type ?? ''}
								onChange={(e) =>
									handleTransactionValueChanged('type', e.currentTarget.value)
								}
							/>
							<Input
								id="debit"
								className="col-span-1 text-right"
								value={transaction.debit ?? 0}
								onChange={(e) =>
									handleTransactionValueChanged('debit', e.currentTarget.value)
								}
							/>
							<Input
								id="note"
								className="col-span-4 mr-1"
								value={transaction.note ?? ''}
								onChange={(e) =>
									handleTransactionValueChanged('note', e.currentTarget.value)
								}
							/>
							<div className="col-span-1 flex flex-row gap-1">
								<StatusButton
									className="bg-blue-700"
									type="submit"
									name="intent"
									value="create-transaction"
									variant="outline"
									onClick={handleSaveNew}
									disabled={saveNewDisabled}
									status={fetcher.state !== 'idle' ? 'pending' : 'idle'}
								>
									<Icon name="save" className="h-4 w-4" />
								</StatusButton>
							</div>
						</div>
						<Separator />
					</div>
				) : null}
				<div className="grid grid-cols-12 gap-1">
					<div className="col-span-1 px-3 text-lg">Display</div>
					<div className="col-span-2 px-3 text-lg">Quickbooks</div>
					<div className="col-span-2 px-3 text-lg">Primary Email</div>
					<div className="col-span-1 px-3 text-lg">Type</div>
					<div className="col-span-1 px-3 text-right text-lg">Amount</div>
					<div className="col-span-4 px-3 text-lg">Note</div>
					<div className="col-span-1 pl-[52px]">
						{userIsAdmin ? <EmailButton all={true} /> : null}
					</div>
				</div>
				{transactions && transactions.length ? (
					transactions.map(
						({
							id: transactionId,
							user: { display, quickbooks, primaryEmail },
							type,
							debit,
							note,
							emailed,
						}) => (
							<div
								key={`row-${transactionId}`}
								className="grid grid-cols-12 gap-1 disabled:cursor-default"
							>
								<Input
									id="display"
									className="col-span-1 disabled:cursor-default"
									disabled={true}
									value={display ?? ''}
								/>
								<Input
									id="quickbooks"
									className="col-span-2 disabled:cursor-default"
									disabled={true}
									value={quickbooks ?? ''}
								/>
								<Input
									id="primaryEmail"
									className="col-span-2 disabled:cursor-default"
									disabled={true}
									value={primaryEmail}
								/>
								<Input
									id="type"
									className="col-span-1 disabled:cursor-default"
									defaultValue={type ?? ''}
									disabled={!userIsAdmin}
									onBlur={(e) =>
										handleChange({
											id: transactionId,
											intent: 'type',
											type: e.currentTarget.value,
										})
									}
								/>
								<Input
									id="debit"
									className="col-span-1 text-right disabled:cursor-default"
									defaultValue={USDollar.format(debit) ?? ''}
									disabled={!userIsAdmin}
									onBlur={(e) =>
										handleChange({
											id: transactionId,
											intent: 'debit',
											debit: e.currentTarget.value,
										})
									}
								/>
								<Input
									id="note"
									className={`col-span-${userIsAdmin ? 4 : 5} mr-1`}
									defaultValue={note ?? ''}
									disabled={!userIsAdmin}
									onBlur={(e) =>
										handleChange({
											id: transactionId,
											intent: 'note',
											note: e.currentTarget.value,
										})
									}
								/>
								{userIsAdmin ? (
									<div className="col-span-1 flex flex-row gap-1">
										<DeleteButton transactionId={transactionId} />
										<EmailButton
											transactionId={transactionId}
											emailed={emailed}
										/>
									</div>
								) : null}
							</div>
						),
					)
				) : (
					<div className="flex w-full justify-center py-4">
						<h4 className="font-medium tracking-wider text-gray-600">
							No results found
						</h4>
					</div>
				)}
			</CardContent>
		</Card>
	)
}

function DeleteButton({ transactionId }: { transactionId: string }) {
	const fetcher = useFetcher<typeof action>()
	const dc = useDoubleCheck()
	return (
		<fetcher.Form method="POST" key={`delete-${transactionId}`}>
			<input type="hidden" name="id" value={transactionId} />
			<StatusButton
				{...dc.getButtonProps({
					type: 'submit',
					name: 'intent',
					value: 'delete-transaction',
				})}
				className={`${dc.doubleCheck ? 'text-destructive' : 'text-primary'}`}
				variant={dc.doubleCheck ? 'default' : 'destructive'}
				status={fetcher.state !== 'idle' ? 'pending' : 'idle'}
			>
				<Icon name="trash" className="h-4 w-4" />
			</StatusButton>
		</fetcher.Form>
	)
}

function EmailButton({
	transactionId,
	emailed,
	all,
}: {
	transactionId?: string
	emailed?: boolean
	all?: boolean
}) {
	const submit = useSubmit()
	const intent = `email-${all ? 'all' : 'transaction'}`
	const handleSendEmailClicked = async () => {
		if (transactionId) {
			await submit({ intent, transactionId }, { method: 'POST' })
		} else {
			await submit({ intent }, { method: 'POST' })
		}
	}
	return (
		<div key={`email-${all ? 'all' : transactionId}`}>
			{emailed ? (
				<Button
					variant="outline"
					className="border-1 border-secondary-foreground bg-green-700 hover:bg-green-700"
				>
					<Icon name="envelope-open" className="h-4 w-4" />
				</Button>
			) : (
				<HoldToConfirmButton
					variant="outline"
					className="border-1 border-secondary-foreground"
					onSubmit={handleSendEmailClicked}
				>
					<Icon name="envelope-closed" className="h-4 w-4" />
				</HoldToConfirmButton>
			)}
		</div>
	)
}
